// /repositories/service.repository.ts
import { safeRequest, AppException } from "../api/safeRequest.ts";
import { toBool, toNumber, toNumOrNull, toStrArray } from "../utils/toNumber.ts";
import { normalizeDisplayPrice, normalizeServiceCard } from "../mappers/service.mapper.ts";
import { ServiceNetwork } from "../api/service.network.ts";
import { useServiceStore } from "../stores/service.store.ts";
import { useCurrencyStore } from "../stores/currency.store.ts";
import type { ServiceSearchResponse } from "../models/service_search.model.ts";
import type { SearchFilterOption } from "../api/service.network.ts";
import { buildServiceSearchKey } from "../utils/serviceSearchKey.ts";
import type { ServiceDetailResponse } from "../models/service_detail.model.ts";

type FetchDashboardOpts = {
  authKey: string;
  force?: boolean;
};

type FetchCategoryServicesOpts = {
  categoryId: string;
  authKey: string;
  force?: boolean;
};

type FetchServiceListOpts = {
  searchOption: SearchFilterOption;
  authKey: string;
  force?: boolean;
};

type FetchServiceDetailOpts = {
  serviceUUID: string;
  authKey: string;
  force?: boolean;
};

function normalizeServiceSearchResponse(res: any): ServiceSearchResponse {
  const data = (res as any)?.data ?? res;

  return {
    search_filter: {
      price_ranges: data?.search_filter?.price_ranges ?? [],
      location_tags: data?.search_filter?.location_tags ?? [],
      count: toNumber(data?.search_filter?.count, 0),
    },
    pagination: {
      total_items: toNumber(data?.pagination?.total_items, 0),
      current_page: toNumber(data?.pagination?.current_page, 1),
      total_pages: toNumber(data?.pagination?.total_pages, 1),
    },
    services: (data?.services ?? []).map((s: any) => ({
      ...s,
      display_price: {
        ...s.display_price,
        // null 은 «가격 미정» — 0 으로 바꾸면 카드가 「From ₩0 ~」 을 그린다.
        price: s?.display_price?.price == null ? null : toNumber(s.display_price.price, 0),
        discount_value:
          s?.display_price?.discount_value == null ? null : toNumber(s.display_price.discount_value, 0),
      },
    })),
  };
}

function normalizeServiceDetailResponse(res: any): ServiceDetailResponse {
  const data = (res as any)?.data ?? res;

  return {
    uuid: String(data?.uuid ?? ""),
    status: toBool(data?.status, false),
    is_coupon_enabled: toBool(data?.is_coupon_enabled, false),

    profile: {
      name: String(data?.profile?.name ?? ""),
      category_id: toNumber(data?.profile?.category_id, 0),
      category: String(data?.profile?.category ?? ""),
      sub_category_id: toNumber(data?.profile?.sub_category_id, 0),
      sub_category: String(data?.profile?.sub_category ?? ""),

      service_closures: data?.profile?.service_closures ?? null,
      service_attributes: (data?.profile?.service_attributes ?? []).map((a: any) => ({
        id: toNumber(a?.id, 0),
        name: String(a?.name ?? ""),
        value: a?.value == null ? null : String(a?.value),
        status: toBool(a?.status, false),
        image: a?.image == null ? null : String(a?.image),
      })),
      images: toStrArray(data?.profile?.images),
    },

    provider: {
      uuid: String(data?.provider?.uuid ?? ""),
      name: String(data?.provider?.name ?? ""),
      contact_name: data?.provider?.contact_name == null ? null : String(data?.provider?.contact_name),
      image: data?.provider?.image == null ? null : String(data?.provider?.image),
      description: data?.provider?.description == null ? null : String(data?.provider?.description),
      provider_review: {
        ratings_average: toNumOrNull(data?.provider?.provider_review?.ratings_average),
        ratings_count: toNumber(data?.provider?.provider_review?.ratings_count, 0),
      },
    },

    descriptions: toStrArray(data?.descriptions),

    service_location: {
      tags: toStrArray(data?.service_location?.tags),
      locations: (data?.service_location?.locations ?? []).map((l: any) => ({
        latitude: toNumber(l?.latitude, 0),
        longitude: toNumber(l?.longitude, 0),
        address: String(l?.address ?? ""),
        address_detail: l?.address_detail ? String(l.address_detail) : null,
        name: String(l?.name ?? ""),
      })),
    },

    service_inclusions: {
      inclusions: toStrArray(data?.service_inclusions?.inclusions),
      exclusions: toStrArray(data?.service_inclusions?.exclusions),
    },

    user_guide: data?.user_guide == null ? null : String(data?.user_guide),
    cancel_policy: data?.cancel_policy == null ? null : String(data?.cancel_policy),

    service_review: {
      ratings_average: toNumOrNull(data?.service_review?.ratings_average),
      ratings_count: toNumber(data?.service_review?.ratings_count, 0),
      reviews: Array.isArray(data?.service_review?.reviews) ? data.service_review.reviews : [],
    },

    display_price: normalizeDisplayPrice(data?.display_price),

    package_service: data?.package_service
      ? {
        uuid: String(data?.package_service?.uuid ?? ""),
        services: (data?.package_service?.services ?? []).map(normalizeServiceCard),
      }
      : null,

    is_bookmarked: toBool(data?.is_bookmarked, false),

    related_services: (data?.related_services ?? []).map((g: any) => ({
      name: String(g?.name ?? "Related"),
      services: (g?.services ?? []).map(normalizeServiceCard),
    })),
  };
}

// 응답이 오는 사이 표시 통화가 바뀌었으면(부팅·로그인 시 계정 통화 동기화 등) 이전 통화
// 금액이다. 그대로 저장하면 기호만 새 통화인 금액(«Rp89» = USD 금액 + IDR 기호)이 남고,
// 진행 중 플래그 때문에 새 통화 재조회도 막혀 있었다 — 버리고 새 통화로 다시 받는다.
const currencyChangedSince = (requested: string) => useCurrencyStore.getState().currency !== requested;

export const ServiceRepository = {
  // ---- search history 삭제 ----
  deleteSearchHistories: async (keywords?: string[]) => {
    const store = useServiceStore.getState();
    store.removeSearchHistories(keywords);
    try {
      await safeRequest(() => ServiceNetwork.deleteSearchHistories(keywords), { handleGlobally: false });
    } catch {
      // 실패 시 무시
    }
  },

  // ---- search suggest (typeahead) — best-effort, never surfaces globally ----
  fetchSearchSuggestions: async (keyword: string): Promise<Array<{ keyword: string; type: string }>> => {
    if (!keyword?.trim()) return [];
    try {
      const res = await safeRequest(() => ServiceNetwork.getSearchSuggestions(keyword.trim()), {
        handleGlobally: false,
      });
      const data = (res as any)?.data ?? res;
      return Array.isArray(data?.suggestions) ? data.suggestions : [];
    } catch {
      return [];
    }
  },

  // ---- search home (추천 키워드 + 검색 기록) ----
  fetchSearchHome: async () => {
    const store = useServiceStore.getState();
    try {
      const res = await safeRequest(() => ServiceNetwork.getSearchHome(), { handleGlobally: false });
      const data = (res as any)?.data ?? res;
      const keywords: string[] = Array.isArray(data?.recommended_keywords)
        ? data.recommended_keywords.map(String).filter(Boolean)
        : [];
      const histories: string[] = Array.isArray(data?.search_histories)
        ? data.search_histories.map(String).filter(Boolean)
        : [];
      store.setSearchHome(keywords, histories);
    } catch {
      // 실패 시 무시
    }
  },

  // ---- dashboard ----
  fetchDashboard: async ({ authKey, force = false }: FetchDashboardOpts) => {
    const store = useServiceStore.getState();

    if (!force && store.hasDashboard && store.dashboardAuthKey === authKey) return store.data;
    if (store.dashboardLoading) return null;

    store.setDashboardLoading(true);
    store.setDashboardError(null);

    try {
      const currency = useCurrencyStore.getState().currency;
      const res = await safeRequest(() => ServiceNetwork.getDashboard(currency), { handleGlobally: true });
      if (currencyChangedSince(currency)) {
        store.setDashboardLoading(false);
        return ServiceRepository.fetchDashboard({ authKey, force: true });
      }
      const payload = (res as any)?.data ?? res;

      store.setFromApi(payload, authKey);
      store.setDashboardLoading(false);
      return payload;
    } catch (e: any) {
      const msg =
        e instanceof AppException ? e.message : e?.response?.data?.message || e?.message || "Failed to load dashboard.";

      store.setDashboardLoading(false);
      store.setDashboardError(msg);
      return null;
    }
  },

  // ---- category ----
  fetchCategoryServices: async ({ categoryId, authKey, force = false }: FetchCategoryServicesOpts) => {
    const store = useServiceStore.getState();
    if (store.categoryLoadingById?.[categoryId]) return null;

    store.setCategoryLoading(categoryId, true);
    store.setCategoryError(categoryId, null);

    try {
      const currency = useCurrencyStore.getState().currency;
      const res = await safeRequest(() => ServiceNetwork.getCategory(categoryId, currency), { handleGlobally: true });
      if (currencyChangedSince(currency)) {
        store.setCategoryLoading(categoryId, false);
        return ServiceRepository.fetchCategoryServices({ categoryId, authKey, force: true });
      }
      const payload = (res as any)?.data ?? res;

      store.setCategoryServices(categoryId, authKey, payload);
      store.setCategoryLoading(categoryId, false);
      return payload;
    } catch (e: any) {
      const msg =
        e instanceof AppException
          ? e.message
          : e?.response?.data?.message || e?.message || "Failed to load category services.";

      store.setCategoryLoading(categoryId, false);
      store.setCategoryError(categoryId, msg);
      return null;
    }
  },

  // ---- search ----
  fetchServiceList: async ({ searchOption, authKey, force = false }: FetchServiceListOpts) => {
    const store = useServiceStore.getState();

    const cacheKey = buildServiceSearchKey(searchOption);
    const requestedPage = searchOption.page ?? 1;

    if (!force && store.hasSearchServices(cacheKey, authKey)) {
      const cached = store.getSearchServices(cacheKey);
      const loadedPage = cached?.pagination?.current_page ?? 0;
      if (loadedPage >= requestedPage) return cached;
      if (!store.canFetchMoreSearchServices(cacheKey)) return cached;
    }

    if (store.searchLoadingByKey?.[cacheKey]) return null;

    store.setSearchLoading(cacheKey, true);
    store.setSearchError(cacheKey, null);

    try {
      const currency = useCurrencyStore.getState().currency;
      const res = await safeRequest(() => ServiceNetwork.getServiceList(searchOption, currency), { handleGlobally: true });
      if (currencyChangedSince(currency)) {
        store.setSearchLoading(cacheKey, false);
        return ServiceRepository.fetchServiceList({ searchOption, authKey, force: true });
      }
      const payload = normalizeServiceSearchResponse(res);

      store.upsertSearchServices(cacheKey, authKey, payload, requestedPage);

      store.setSearchLoading(cacheKey, false);
      return store.getSearchServices(cacheKey);
    } catch (e: any) {
      const msg =
        e instanceof AppException ? e.message : e?.response?.data?.message || e?.message || "Failed to load services.";

      store.setSearchLoading(cacheKey, false);
      store.setSearchError(cacheKey, msg);
      return null;
    }
  },

  // ---- detail ----
  fetchServiceDetail: async ({ serviceUUID, authKey, force = false }: FetchServiceDetailOpts) => {
    const store = useServiceStore.getState();
    if (!serviceUUID) return null;

    if (!force && store.hasServiceDetail(serviceUUID, authKey)) {
      return store.getServiceDetail(serviceUUID);
    }

    if (store.detailLoadingByUuid?.[serviceUUID]) return null;

    store.setDetailLoading(serviceUUID, true);
    store.setDetailError(serviceUUID, null);

    try {
      const currency = useCurrencyStore.getState().currency;
      const res = await safeRequest(() => ServiceNetwork.getServiceDetail(serviceUUID, currency), { handleGlobally: true });
      if (currencyChangedSince(currency)) {
        store.setDetailLoading(serviceUUID, false);
        return ServiceRepository.fetchServiceDetail({ serviceUUID, authKey, force: true });
      }
      const payload: ServiceDetailResponse = normalizeServiceDetailResponse(res);

      store.setServiceDetail(serviceUUID, authKey, payload);

      store.setDetailLoading(serviceUUID, false);
      return payload;
    } catch (e: any) {
      const msg =
        e instanceof AppException
          ? e.message
          : e?.response?.data?.message || e?.message || "Failed to load service detail.";

      store.setDetailLoading(serviceUUID, false);
      store.setDetailError(serviceUUID, msg);
      return null;
    }
  },
};
